import { useState, useEffect, useCallback, useRef, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ChevronRight, ChevronDown, Search, Layers, BookOpen, ClipboardList, AlertTriangle,
  Plus, Pencil, Users, MoreHorizontal, ChevronsUpDown, Info, X, Loader2,
  Circle, Dot, Trash2, Check, FileQuestion, MessageSquare, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import RichTextEditor from "@/components/RichTextEditor";
import DuplicationWizard from "@/components/DuplicationWizard";
import usePersistentState from "@/hooks/usePersistentState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── helpers ─── */
function hashColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hues = [210, 340, 160, 30, 270, 190, 50, 300, 120, 0];
  return `hsl(${hues[Math.abs(hash) % hues.length]}, 55%, 50%)`;
}

function statusBadge(status) {
  const map = {
    published: { bg: "#e6faf8", text: "#0d9488", label: "Published" },
    draft: { bg: "#fef3cd", text: "#92751a", label: "Draft" },
    incomplete: { bg: "#fee2e2", text: "#dc2626", label: "Incomplete" },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-body font-medium whitespace-nowrap"
      style={{ backgroundColor: s.bg, color: s.text }}
      data-testid={`status-badge-${status}`}
    >
      {s.label}
    </span>
  );
}

function itemTypeTag(type) {
  const labels = { mc: "MC", sata: "SATA", task: "Task", likert: "Likert", external_score: "External Score", external_item: "External Item", multiple_choice: "MC", select_all: "SATA" };
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-body font-medium"
      style={{ backgroundColor: "rgba(6,37,70,0.08)", color: "#062546" }}
    >
      {labels[type] || type || "Item"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

/* ─── DefinitionsPopover ─── */
function DefinitionsPopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-body font-medium transition-colors hover:bg-[#062546]/10"
        style={{ color: "#062546", border: "1px solid #e2e6ef" }}
        data-testid="definitions-info-btn"
        aria-label="Definitions"
      >
        <Info className="h-3.5 w-3.5" />
        Definitions
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 w-96 p-5 rounded-xl text-xs font-body leading-relaxed space-y-3"
          style={{ backgroundColor: "#fff", color: "#062546", border: "1px solid #e2e6ef", boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
          data-testid="definitions-popover"
        >
          <div>
            <span className="font-bold text-sm">Track</span>
            <p className="mt-0.5" style={{ color: "#5a6577" }}>The full learning experience for a given role, rotation, or competency. It contains LEXPs in sequence and belongs to a cohort.</p>
          </div>
          <div>
            <span className="font-bold text-sm">LEXP <span className="font-normal" style={{ color: "#8896a7" }}>(Learning Experience)</span></span>
            <p className="mt-0.5" style={{ color: "#5a6577" }}>One chapter inside a Track. It has a single focus — one clinical scenario, one workflow, one concept. It could be a guided walkthrough, a simulation the learner operates inside, or a structured assessment. The learner experiences it as a whole thing. Also known as an "activity."</p>
          </div>
          <div>
            <span className="font-bold text-sm">Item</span>
            <p className="mt-0.5" style={{ color: "#5a6577" }}>The individual steps or questions inside a LEXP. These are what the learner completes or what the platform tracks as evidence that learning happened. Items can be tasks tied to an external test (e.g., if this LEXP is a companion to a software sandbox), questions inside an assessment, or steps completed directly inside a LEXP.</p>
          </div>
          <div className="pt-2" style={{ borderTop: "1px solid #e2e6ef" }}>
            <p className="font-semibold" style={{ color: "#062546" }}>A Track tells you what to learn. A LEXP is where you learn it. An item is proof that you did.</p>
          </div>
        </div>
      )}
    </span>
  );
}

/* ─── ItemList (lazy-loaded) ─── */
function ItemList({ lexpId, itemCount }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (items !== null) return;
    setLoading(true);
    axios.get(`${API}/learning-experiences/${lexpId}/items`, { withCredentials: true })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [lexpId, items]);

  if (loading) return <div className="py-3 pl-14 text-xs font-body" style={{ color: "#8896a7" }}>Loading items...</div>;
  if (!items || items.length === 0) return <div className="py-3 pl-14 text-xs font-body" style={{ color: "#8896a7" }}>No items in this LEXP.</div>;

  const LIMIT = 5;
  const visible = showAll ? items : items.slice(0, Math.min(LIMIT - 1, items.length));
  const remaining = items.length - visible.length;

  return (
    <div className="py-2 pl-14 pr-4 space-y-0.5" data-testid="item-list">
      <div className="text-[10px] font-body uppercase tracking-wider mb-2" style={{ color: "#8896a7" }}>
        Admin view — platform measurement items
      </div>
      {visible.map((item) => (
        <div
          key={item.id}
          className="group flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-black/[0.02] transition-colors"
          data-testid={`item-row-${item.id}`}
        >
          <Dot className="h-4 w-4 shrink-0" style={{ color: "#8896a7" }} />
          <span className="text-xs font-body flex-1 truncate" style={{ color: "#062546" }}>
            {item.title || item.stem?.replace(/<[^>]*>/g, "").slice(0, 60) || "Untitled Item"}
          </span>
          {itemTypeTag(item.item_type)}
          <button
            onClick={(e) => { e.stopPropagation(); window.open(`/admin/items/${item.id}`, "_blank"); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/5"
            data-testid={`item-edit-${item.id}`}
          >
            <Pencil className="h-3 w-3" style={{ color: "#8896a7" }} />
          </button>
        </div>
      ))}
      {!showAll && remaining > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="text-xs font-body font-medium py-1.5 px-2 rounded-md hover:bg-black/[0.02] transition-colors w-full text-left"
          style={{ color: "var(--primary-color, #09beb2)" }}
          data-testid="show-more-items"
        >
          + {remaining} more item{remaining > 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

/* ─── LEXPRow ─── */
function LEXPRow({ child, index, isExpanded, onToggle }) {
  const hasNoItems = child.item_count === 0;
  const derivedStatus = hasNoItems ? "incomplete" : (child.status || "draft");

  return (
    <div data-testid={`lexp-row-${child.ref_id}`}>
      <div
        className="group flex items-center gap-3 py-3 px-3 cursor-pointer transition-colors hover:bg-black/[0.02] rounded-lg"
        style={{ borderLeft: hasNoItems ? "3px solid #dc2626" : "3px solid transparent" }}
        onClick={onToggle}
      >
        {/* Chevron */}
        <span className="shrink-0 w-4 h-4 flex items-center justify-center" style={{ color: "#8896a7" }}>
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </span>
        {/* Sequence circle */}
        <span
          className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body font-semibold"
          style={{
            backgroundColor: isExpanded ? "#062546" : "transparent",
            color: isExpanded ? "#fff" : "#8896a7",
            border: isExpanded ? "none" : "1.5px solid #c5cbd6",
          }}
        >
          {index + 1}
        </span>
        {/* Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-body font-semibold truncate" style={{ color: "#062546" }} data-testid="lexp-name">
              {child.title}
            </span>
            {child.lexp_type && (
              <span className="text-[10px] font-body px-1.5 py-0.5 rounded capitalize" style={{ backgroundColor: "#f0f2f5", color: "#5a6577" }}>
                {child.lexp_type}
              </span>
            )}
          </div>
          {hasNoItems && (
            <span className="text-[11px] font-body" style={{ color: "#dc2626" }}>No items yet</span>
          )}
        </div>
        {/* Item count */}
        <span className="text-xs font-body whitespace-nowrap" style={{ color: "#8896a7" }}>
          {child.item_count} item{child.item_count !== 1 ? "s" : ""}
        </span>
        {/* Status */}
        {statusBadge(derivedStatus)}
        {/* Hover actions */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/admin/learning-experiences/${child.ref_id}`; }}
            className="p-1 rounded hover:bg-black/5" data-testid={`lexp-edit-${child.ref_id}`}
          >
            <Pencil className="h-3.5 w-3.5" style={{ color: "#8896a7" }} />
          </button>
        </div>
      </div>
      {/* Expanded: item list */}
      {isExpanded && child.type === "lexp" && (
        <ItemList lexpId={child.ref_id} itemCount={child.item_count} />
      )}
    </div>
  );
}

/* ─── TrackCard ─── */
function TrackCard({ track, isExpanded, onToggleTrack, expandedLexp, onToggleLexp, onOpenWizard, onAssignCohort }) {
  const accent = hashColor(track.id);

  return (
    <div
      className="rounded-xl overflow-hidden transition-shadow"
      style={{ border: "1px solid #e2e6ef", backgroundColor: "#fff" }}
      data-testid={`track-card-${track.id}`}
    >
      {/* Header */}
      <div
        className="group flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none hover:bg-black/[0.01] transition-colors"
        onClick={() => onToggleTrack(track.id)}
        data-testid={`track-header-${track.id}`}
      >
        {/* Color bar */}
        <div className="w-1 self-stretch rounded-full shrink-0" style={{ backgroundColor: accent }} />
        {/* Chevron */}
        <span style={{ color: "#5a6577" }}>
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        {/* Name */}
        <div className="flex-1 min-w-0">
          <span className="text-base font-body font-bold truncate block" style={{ color: "#062546" }} data-testid="track-name">
            {track.title}
          </span>
          <span className="text-xs font-body mt-0.5 block" style={{ color: "#8896a7" }}>Edited {formatDate(track.updated_at)}</span>
        </div>
        {/* Counts */}
        <span className="text-xs font-body whitespace-nowrap" style={{ color: "#5a6577" }}>
          {track.lexp_count} LEXP{track.lexp_count !== 1 ? "s" : ""}, {track.total_item_count} item{track.total_item_count !== 1 ? "s" : ""}
        </span>
        {/* Status */}
        {statusBadge(track.derived_status)}
        {/* Hover actions */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/admin/tracks/${track.id}`; }}
            className="p-1.5 rounded-md hover:bg-black/5" data-testid={`track-edit-${track.id}`}
          >
            <Pencil className="h-3.5 w-3.5" style={{ color: "#5a6577" }} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onAssignCohort?.(track.id); }} className="p-1.5 rounded-md hover:bg-black/5" data-testid={`track-assign-${track.id}`}>
            <Users className="h-3.5 w-3.5" style={{ color: "#5a6577" }} />
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className="border-t" style={{ borderColor: "#e2e6ef" }}>
          {/* Incomplete banner */}
          {track.incomplete_lexp_count > 0 && (
            <div
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-body"
              style={{ backgroundColor: "#fffbeb", color: "#92751a", borderBottom: "1px solid #fde68a" }}
              data-testid="incomplete-banner"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {track.incomplete_lexp_count} LEXP{track.incomplete_lexp_count !== 1 ? "s have" : " has"} no items yet.
              This Track cannot be published until all LEXPs are complete.
            </div>
          )}

          {/* LEXP list */}
          <div className="px-3 py-2">
            {track.children.filter((c) => c.type === "lexp").map((child, idx) => (
              <LEXPRow
                key={child.ref_id}
                child={child}
                index={idx}
                isExpanded={expandedLexp === child.ref_id}
                onToggle={() => onToggleLexp(track.id, child.ref_id)}
              />
            ))}
            {/* Assessment children (render inline, non-expandable) */}
            {track.children.filter((c) => c.type === "assessment").map((child) => (
              <div
                key={child.ref_id}
                className="group flex items-center gap-3 py-3 px-3 rounded-lg hover:bg-black/[0.02] transition-colors"
                style={{ borderLeft: "3px solid transparent" }}
                data-testid={`assessment-row-${child.ref_id}`}
              >
                <span className="shrink-0 w-4" />
                <ClipboardList className="h-4 w-4 shrink-0" style={{ color: "#8896a7" }} />
                <span className="text-sm font-body font-semibold flex-1 truncate" style={{ color: "#062546" }}>{child.title}</span>
                <span className="text-xs font-body" style={{ color: "#8896a7" }}>{child.item_count} items</span>
                {statusBadge(child.status || "published")}
              </div>
            ))}
            {/* Add LEXP row */}
            <button
              className="flex items-center gap-2 w-full py-3 px-3 rounded-lg text-xs font-body font-medium transition-colors hover:bg-black/[0.02]"
              style={{ color: "var(--primary-color, #09beb2)" }}
              onClick={() => onOpenWizard("add_lexp", track.id)}
              data-testid={`add-lexp-to-track-${track.id}`}
            >
              <Plus className="h-3.5 w-3.5" />
              Add LEXP to this Track
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── WizardModal ─── */
const DEFAULT_OPT = () => ({ option_id: Math.random().toString(36).slice(2, 14), text: "", is_correct: false, point_value: 0 });
const WIZARD_INIT = {
  step: 1, intent: null, saving: false,
  // Survey inline creation
  surveyTitle: "", surveyDescription: "", surveyAnonymous: false, surveyAllowResubmit: false,
  trackId: null, trackName: "",
  lexps: [],
  currentLexpId: null, currentLexpTitle: "", currentLexpContent: "",
  currentItemType: "mc", currentItemTitle: "", currentItemStem: "",
  currentItemOptions: [DEFAULT_OPT(), DEFAULT_OPT()],
  currentItemRandomize: true, currentItemPointValue: 1, currentItemCategory: "assessment",
  currentItemLikertMin: 0, currentItemLikertMax: 5, currentItemLikertLabels: {},
  currentItemExtCorrect: 0, currentItemExtTotal: 0, currentItemScoringSource: "native",
  currentLexpItems: [],
  assignCohort: null, cohortId: null,
  assessmentName: "", assessmentSource: "native",
  // External assessment flow
  extAssessmentMode: null, // "score" or "items"
  extScoreCorrect: 0, extScoreTotal: 0,
  extAssessmentTypeId: null,
  extItems: [], // created external items [{id, title, point_value}]
  extCurrentTitle: "", extCurrentItemId: "", extCurrentPointValue: 1,
  dirty: false,
};
function wizardReducer(st, action) {
  switch (action.type) {
    case "SET": return { ...st, ...action.payload, dirty: true };
    case "RESET": return { ...WIZARD_INIT };
    case "RESET_ITEM": return { ...st, currentItemType: "mc", currentItemTitle: "", currentItemStem: "", currentItemOptions: [DEFAULT_OPT(), DEFAULT_OPT()], currentItemRandomize: true, currentItemPointValue: 1, currentItemCategory: "assessment", currentItemLikertMin: 0, currentItemLikertMax: 5, currentItemLikertLabels: {}, currentItemExtCorrect: 0, currentItemExtTotal: 0, currentItemScoringSource: "native" };
    case "RESET_LEXP": return { ...st, currentLexpId: null, currentLexpTitle: "", currentLexpContent: "", currentLexpItems: [], currentItemType: "mc", currentItemTitle: "", currentItemStem: "", currentItemOptions: [DEFAULT_OPT(), DEFAULT_OPT()], currentItemRandomize: true, currentItemPointValue: 1, currentItemCategory: "assessment", currentItemLikertMin: 0, currentItemLikertMax: 5, currentItemLikertLabels: {}, currentItemExtCorrect: 0, currentItemExtTotal: 0, currentItemScoringSource: "native" };
    case "ADD_ITEM": return { ...st, currentLexpItems: [...st.currentLexpItems, action.payload] };
    case "ADD_EXT_ITEM": return { ...st, extItems: [...st.extItems, action.payload], extCurrentTitle: "", extCurrentItemId: "", extCurrentPointValue: 1 };
    case "FINISH_LEXP": return { ...st, lexps: [...st.lexps, { id: st.currentLexpId, title: st.currentLexpTitle, itemCount: st.currentLexpItems.length }] };
    default: return st;
  }
}
function StepDots({ current, total }) {
  return (<div className="flex items-center gap-1.5 mt-3" data-testid="wizard-step-dots">{Array.from({ length: total }, (_, i) => (<div key={i} className="w-2 h-2 rounded-full transition-colors" style={{ backgroundColor: i < current ? "#062546" : "#e2e6ef" }} />))}</div>);
}

function WizardModal({ open, onClose, preselect, onCreated }) {
  const [state, dispatch] = useReducer(wizardReducer, WIZARD_INIT);
  const [cohorts, setCohorts] = useState([]);
  const [cohortSearch, setCohortSearch] = useState("");
  const [confirmExit, setConfirmExit] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { if (open) { dispatch({ type: "RESET" }); setConfirmExit(false); if (preselect) dispatch({ type: "SET", payload: { intent: preselect } }); } }, [open, preselect]);
  const tryClose = () => { if (state.dirty && state.step > 1) { setConfirmExit(true); return; } onClose(false); };
  const handleDialogChange = (v) => { if (!v) tryClose(); };
  const s = state;
  const d = (p) => dispatch({ type: "SET", payload: p });
  const isTrackFlow = s.intent === "new_track";
  const isAssessmentFlow = s.intent === "new_assessment";
  const isLexpFlow = s.intent === "new_lexp";
  const isItemFlow = s.intent === "new_item";
  const isSurveyFlow = s.intent === "new_survey";
  const isStubFlow = isLexpFlow || isItemFlow;
  const totalDots = isTrackFlow ? 7 : isAssessmentFlow ? 5 : 2;

  const stubTargetFor = (intent) => {
    switch (intent) {
      case "new_lexp": return { path: "/admin/learning-experiences/new", label: "Learning Experience builder", noun: "Learning Experience" };
      case "new_item": return { path: "/admin/items/new", label: "Item builder (Question Bank)", noun: "Item" };
      default: return null;
    }
  };
  const goToStubTarget = () => {
    const t = stubTargetFor(s.intent);
    if (!t) return;
    onClose(false);
    navigate(t.path);
  };

  const createSurveyInline = async () => {
    if (!s.surveyTitle?.trim()) { toast.error("Title is required"); return; }
    d({ saving: true });
    try {
      const { data } = await axios.post(`${API}/surveys`, {
        title: s.surveyTitle.trim(),
        description: (s.surveyDescription || "").trim(),
        anonymous: !!s.surveyAnonymous,
        allow_resubmit: !!s.surveyAllowResubmit,
      }, { withCredentials: true });
      toast.success("Survey created");
      onClose(false);
      onCreated?.();
      navigate(`/admin/surveys/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create survey");
    } finally {
      d({ saving: false });
    }
  };

  const updateOpt = (idx, field, val) => { const opts = [...s.currentItemOptions]; opts[idx] = { ...opts[idx], [field]: val }; if (field === "is_correct" && val && s.currentItemType === "mc") opts.forEach((o, i) => { if (i !== idx) o.is_correct = false; }); d({ currentItemOptions: opts }); };
  const addOpt = () => d({ currentItemOptions: [...s.currentItemOptions, DEFAULT_OPT()] });
  const removeOpt = (idx) => { if (s.currentItemOptions.length <= 2) return; d({ currentItemOptions: s.currentItemOptions.filter((_, i) => i !== idx) }); };
  const likertRange = []; for (let i = s.currentItemLikertMin; i <= s.currentItemLikertMax; i++) likertRange.push(i);
  const isExtScore = s.currentItemType === "external_score";
  const computedPV = isExtScore ? (s.currentItemExtTotal > 0 ? +(s.currentItemExtCorrect / s.currentItemExtTotal).toFixed(4) : 0) : (s.currentItemType === "task" || s.currentItemType === "likert" || s.currentItemCategory === "survey") ? 0 : (s.currentItemType === "mc" || s.currentItemType === "sata") ? s.currentItemOptions.reduce((sum, o) => sum + (o.point_value || 0), 0) : s.currentItemPointValue;

  const createTrack = async () => { d({ saving: true }); try { const { data } = await axios.post(`${API}/tracks`, { title: s.trackName.trim(), status: "draft" }, { withCredentials: true }); d({ trackId: data.id, step: 3, saving: false }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); } };
  const createLexp = async () => { d({ saving: true }); try { const { data } = await axios.post(`${API}/learning-experiences`, { title: s.currentLexpTitle.trim(), description: "", patient_scenario: s.currentLexpContent || "", tasks: "", status: "draft" }, { withCredentials: true }); await axios.post(`${API}/tracks/${s.trackId}/items`, { type: "lexp", ref_id: data.id }, { withCredentials: true }); d({ currentLexpId: data.id, step: 4, saving: false }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); } };
  const createItem = async () => {
    if (!s.currentItemTitle.trim()) { toast.error("Title required"); return; }
    if (!isExtScore && !s.currentItemStem.trim()) { toast.error("Stem required"); return; }
    if (s.currentItemType === "mc" && !s.currentItemOptions.some((o) => o.is_correct)) { toast.error("Select correct answer"); return; }
    if (isExtScore && s.currentItemExtTotal <= 0) { toast.error("Total must be > 0"); return; }
    d({ saving: true });
    try {
      const payload = { title: s.currentItemTitle.trim(), item_type: s.currentItemType, stem: isExtScore ? "" : s.currentItemStem, answer_options: (s.currentItemType === "task" || s.currentItemType === "likert" || isExtScore) ? [] : s.currentItemOptions, randomize_options: s.currentItemRandomize, point_value: computedPV, item_category: s.currentItemCategory, status: "published" };
      if (s.currentItemType === "likert") { payload.likert_scale_min = s.currentItemLikertMin; payload.likert_scale_max = s.currentItemLikertMax; payload.likert_labels = s.currentItemLikertLabels; }
      if (isExtScore) { payload.correct_answers = s.currentItemExtCorrect; payload.total_answers = s.currentItemExtTotal; }
      const { data } = await axios.post(`${API}/items`, payload, { withCredentials: true });
      await axios.post(`${API}/learning-experiences/${s.currentLexpId}/items`, { item_id: data.id }, { withCredentials: true });
      dispatch({ type: "ADD_ITEM", payload: { id: data.id, title: data.title, type: data.item_type } });
      dispatch({ type: "RESET_ITEM" });
      d({ saving: false }); toast.success("Item created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); }
  };
  const finishLexp = () => { dispatch({ type: "FINISH_LEXP" }); d({ step: 5 }); };
  const anotherLexp = () => { dispatch({ type: "RESET_LEXP" }); d({ step: 3 }); };
  const loadCohorts = async () => { try { const { data } = await axios.get(`${API}/cohorts`, { withCredentials: true }); setCohorts(data); } catch {} };
  const assignCohort = async () => { if (!s.cohortId) { d({ step: 7 }); return; } d({ saving: true }); try { await axios.post(`${API}/cohorts/${s.cohortId}/tracks`, { track_id: s.trackId }, { withCredentials: true }); d({ step: 7, saving: false }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); } };
  const publishTrack = async () => { d({ saving: true }); try { await axios.put(`${API}/tracks/${s.trackId}`, { status: "published" }, { withCredentials: true }); toast.success("Track published!"); onClose(false); onCreated?.(s.trackId); } catch (e) { toast.error("Failed"); d({ saving: false }); } };
  const saveDraft = () => { toast.success("Track saved as draft"); onClose(false); onCreated?.(s.trackId); };
  const createAssessment = async () => {
    if (!s.assessmentName.trim()) { toast.error("Name required"); return; }
    d({ saving: true });
    try {
      const kind = s.assessmentSource === "external" ? "external" : "native";
      const { data } = await axios.post(`${API}/assessment-types`, { name: s.assessmentName.trim(), source: s.assessmentSource, input_method: "entry", item_level: true, assessment_kind: kind }, { withCredentials: true });
      if (s.assessmentSource === "native") {
        toast.success("Assessment created");
        d({ step: 5, saving: false, extAssessmentTypeId: data.id });
      } else if (s.extAssessmentMode === "score") {
        // Create a single external_score item and link it
        const itemResp = await axios.post(`${API}/items`, {
          title: s.assessmentName.trim() + " — Score", item_type: "external_score", stem: "", answer_options: [],
          point_value: s.extScoreTotal > 0 ? +(s.extScoreCorrect / s.extScoreTotal).toFixed(4) : 0,
          correct_answers: s.extScoreCorrect, total_answers: s.extScoreTotal,
          item_category: "assessment", scoring_source: "external", status: "published",
        }, { withCredentials: true });
        await axios.post(`${API}/assessment-types/${data.id}/items`, { item_id: itemResp.data.id }, { withCredentials: true });
        d({ step: 5, saving: false, extAssessmentTypeId: data.id });
      } else {
        // Items mode — assessment created, proceed to item loop
        d({ step: 4, saving: false, extAssessmentTypeId: data.id });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); }
  };

  const createExtItem = async () => {
    if (!s.extCurrentTitle.trim()) { toast.error("Title required"); return; }
    if (s.extCurrentPointValue <= 0) { toast.error("Point value must be > 0"); return; }
    d({ saving: true });
    try {
      const { data } = await axios.post(`${API}/items`, {
        title: s.extCurrentTitle.trim(), item_type: "external_item", stem: "", answer_options: [],
        point_value: s.extCurrentPointValue, item_category: "assessment", status: "published",
        item_id: s.extCurrentItemId.trim() || undefined,
      }, { withCredentials: true });
      await axios.post(`${API}/assessment-types/${s.extAssessmentTypeId}/items`, { item_id: data.id }, { withCredentials: true });
      dispatch({ type: "ADD_EXT_ITEM", payload: { id: data.id, title: data.title, point_value: data.point_value } });
      d({ saving: false }); toast.success("Item created");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); d({ saving: false }); }
  };

  const cohortObj = cohorts.find((c) => (c.id || String(c._id)) === s.cohortId);
  const filteredCohorts = cohorts.filter((c) => c.name?.toLowerCase().includes(cohortSearch.toLowerCase()));

  const renderStep = () => {
    if (s.step === 1) {
      const choices = [
        { id: "new_track", icon: Layers, title: "A new Track", desc: "A full course containing learning experiences and items" },
        { id: "new_lexp", icon: BookOpen, title: "A new LEXP", desc: "A standalone learning experience" },
        { id: "new_item", icon: FileQuestion, title: "A new Item", desc: "A single question or task added to the Question Bank" },
        { id: "new_survey", icon: MessageSquare, title: "A new Survey", desc: "A feedback form that can be added to a track" },
      ];
      return (<><p className="text-sm font-body mt-4 mb-5" style={{ color: "#5a6577" }}>Pick what you want to create. Each option opens the right builder.</p><div className="space-y-3">{choices.map((c) => (<button key={c.id} onClick={() => d({ intent: c.id })} className="w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all" style={{ border: s.intent === c.id ? "2px solid #062546" : "1.5px solid #e2e6ef", backgroundColor: s.intent === c.id ? "#f5f7fa" : "#fff" }} data-testid={`wizard-choice-${c.id}`}><div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5" style={{ backgroundColor: s.intent === c.id ? "#062546" : "#f0f2f5" }}><c.icon className="h-4 w-4" style={{ color: s.intent === c.id ? "#fff" : "#5a6577" }} /></div><div><span className="text-sm font-body font-bold block" style={{ color: "#062546" }}>{c.title}</span><span className="text-xs font-body mt-0.5 block" style={{ color: "#8896a7" }}>{c.desc}</span></div></button>))}</div></>);
    }
    if (isSurveyFlow && s.step === 2) {
      return (
        <div className="space-y-4 mt-4" data-testid="wizard-survey-form">
          <p className="text-sm font-body" style={{ color: "#5a6577" }}>
            Give your survey a title. You'll add questions in the Survey Builder after this.
          </p>
          <div className="space-y-1.5">
            <Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Title</Label>
            <Input
              value={s.surveyTitle}
              onChange={(e) => d({ surveyTitle: e.target.value, dirty: true })}
              placeholder="e.g. Post-Track Feedback"
              className="bg-white font-body text-sm"
              style={{ borderColor: "#e2e6ef", color: "#062546" }}
              data-testid="wizard-survey-title"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Description <span className="text-[10px] font-normal" style={{ color: "#8896a7" }}>(optional)</span></Label>
            <Input
              value={s.surveyDescription}
              onChange={(e) => d({ surveyDescription: e.target.value, dirty: true })}
              placeholder="Short description shown to learners"
              className="bg-white font-body text-sm"
              style={{ borderColor: "#e2e6ef", color: "#062546" }}
              data-testid="wizard-survey-description"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg" style={{ border: "1px solid #e2e6ef", backgroundColor: "#fafbfc" }}>
            <div>
              <p className="text-sm font-body font-medium" style={{ color: "#062546" }}>Anonymous</p>
              <p className="text-[11px] font-body" style={{ color: "#8896a7" }}>Responses are not tied to a learner identity</p>
            </div>
            <Switch checked={s.surveyAnonymous} onCheckedChange={(v) => d({ surveyAnonymous: v, dirty: true })} data-testid="wizard-survey-anonymous" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg" style={{ border: "1px solid #e2e6ef", backgroundColor: "#fafbfc" }}>
            <div>
              <p className="text-sm font-body font-medium" style={{ color: "#062546" }}>Allow resubmit</p>
              <p className="text-[11px] font-body" style={{ color: "#8896a7" }}>Learners can submit multiple times</p>
            </div>
            <Switch checked={s.surveyAllowResubmit} onCheckedChange={(v) => d({ surveyAllowResubmit: v, dirty: true })} data-testid="wizard-survey-allow-resubmit" />
          </div>
        </div>
      );
    }
    if (isStubFlow && s.step === 2) {
      const t = stubTargetFor(s.intent);
      return (
        <div className="space-y-4 mt-4" data-testid={`wizard-stub-${s.intent}`}>
          <div className="rounded-xl p-5" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}>
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#062546" }}>
                <ExternalLink className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-body font-bold" style={{ color: "#062546" }}>You'll be taken to the builder</p>
                <p className="text-xs font-body mt-1" style={{ color: "#5a6577" }}>
                  Creating a new {t?.noun} opens the dedicated {t?.label}. Click Continue to open it now.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (isTrackFlow && s.step === 2) return (<div className="space-y-4 mt-4"><p className="text-sm font-body" style={{ color: "#5a6577" }}>Give your Track a name.</p><div className="space-y-1.5"><Label className="font-body text-sm font-medium" style={{ color: "#062546" }}>Track Name <span className="text-red-500">*</span></Label><Input value={s.trackName} onChange={(e) => d({ trackName: e.target.value })} placeholder="e.g. Inpatient Nurse Residency" autoFocus className="h-10 font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-track-name" /></div></div>);
    if (isTrackFlow && s.step === 3) return (<div className="space-y-4 mt-4"><p className="text-sm font-body" style={{ color: "#5a6577" }}>{s.lexps.length === 0 ? "Create the first activity (LEXP) in this Track." : `Add another activity. You have ${s.lexps.length} so far.`}</p><div className="space-y-1.5"><Label className="font-body text-sm font-medium" style={{ color: "#062546" }}>LEXP Title <span className="text-red-500">*</span></Label><Input value={s.currentLexpTitle} onChange={(e) => d({ currentLexpTitle: e.target.value })} placeholder="e.g. Vital Signs Documentation" autoFocus className="h-10 font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-lexp-title" /></div><div className="space-y-1.5"><Label className="font-body text-sm font-medium" style={{ color: "#062546" }}>Learning Content <span className="text-xs font-normal" style={{ color: "#8896a7" }}>(optional)</span></Label><div style={{ border: "1px solid #e2e6ef", borderRadius: "8px", overflow: "hidden" }}><RichTextEditor value={s.currentLexpContent} onChange={(v) => d({ currentLexpContent: v })} placeholder="Scenario, instructions, or context..." testId="wizard-lexp-content" /></div></div></div>);
    if (isTrackFlow && s.step === 4) return (
      <div className="space-y-4 mt-3" style={{ maxHeight: "55vh", overflowY: "auto" }}>
        {s.currentLexpItems.length > 0 && (<div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}><div className="flex items-center gap-2"><Check className="h-3.5 w-3.5" style={{ color: "#0d9488" }} /><span className="text-xs font-body font-semibold" style={{ color: "#062546" }}>{s.currentLexpItems.length} item{s.currentLexpItems.length > 1 ? "s" : ""} created</span></div>{s.currentLexpItems.map((it) => (<span key={it.id} className="text-xs font-body block pl-5" style={{ color: "#8896a7" }}>{it.title} ({it.type})</span>))}</div>)}
        <p className="text-sm font-body" style={{ color: "#5a6577" }}>{s.currentLexpItems.length === 0 ? "Add at least one item to this LEXP." : "Add another item, or finish this LEXP."}</p>
        <div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Item Type</Label><div className="flex gap-1.5 flex-wrap">{[{ v: "mc", l: "MC" }, { v: "sata", l: "SATA" }, { v: "task", l: "Task" }, { v: "likert", l: "Likert" }, { v: "external_score", l: "Ext Score" }, { v: "external_item", l: "Ext Item" }].map((t) => (<button key={t.v} type="button" onClick={() => d({ currentItemType: t.v })} className="px-3 py-1.5 text-xs font-body rounded-lg border transition-colors" style={{ borderColor: s.currentItemType === t.v ? "#062546" : "#e2e6ef", backgroundColor: s.currentItemType === t.v ? "#062546" : "#fff", color: s.currentItemType === t.v ? "#fff" : "#5a6577" }} data-testid={`wizard-item-type-${t.v}`}>{t.l}</button>))}</div></div>
        <div className="space-y-1.5"><Label className="font-body text-xs font-medium flex items-center gap-1.5" style={{ color: "#062546" }}>Category <span className="text-[10px] font-normal" style={{ color: "#8896a7" }}>(this item's role inside the LEXP — not a track-level Survey stop)</span></Label><div className="flex gap-1.5">{[{ v: "assessment", l: "Assessment" }, { v: "survey", l: "Survey Question" }].map((c) => (<button key={c.v} type="button" onClick={() => !isExtScore && d({ currentItemCategory: c.v })} title={c.v === "survey" ? "Unscored feedback item embedded in this LEXP. To add a standalone Survey stop to a Track, use the Survey Library." : "Scored native item."} className="px-3 py-1.5 text-xs font-body rounded-full border transition-colors" style={{ borderColor: s.currentItemCategory === c.v ? "#062546" : "#e2e6ef", backgroundColor: s.currentItemCategory === c.v ? "#062546" : "#fff", color: s.currentItemCategory === c.v ? "#fff" : "#5a6577", opacity: isExtScore && c.v === "survey" ? 0.35 : 1, cursor: isExtScore && c.v === "survey" ? "not-allowed" : "pointer" }} data-testid={`wizard-item-category-${c.v}`}>{c.l}</button>))}</div></div>
        <div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Title <span className="text-red-500">*</span></Label><Input value={s.currentItemTitle} onChange={(e) => d({ currentItemTitle: e.target.value })} placeholder="e.g. Q1 — Vital Signs" className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-item-title" /></div>
        {!isExtScore && (<div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Stem <span className="text-red-500">*</span></Label><div style={{ border: "1px solid #e2e6ef", borderRadius: "8px", overflow: "hidden" }}><RichTextEditor value={s.currentItemStem} onChange={(v) => d({ currentItemStem: v })} placeholder="Question or task description..." testId="wizard-item-stem" /></div></div>)}
        {isExtScore && (<div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Correct</Label><Input type="number" min="0" value={s.currentItemExtCorrect} onChange={(e) => d({ currentItemExtCorrect: parseFloat(e.target.value) || 0 })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} /></div><div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Total</Label><Input type="number" min="1" value={s.currentItemExtTotal} onChange={(e) => d({ currentItemExtTotal: parseFloat(e.target.value) || 0 })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} /></div></div>)}
        {(s.currentItemType === "mc" || s.currentItemType === "sata") && (<div className="space-y-2"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Answer Options</Label>{s.currentItemOptions.map((opt, idx) => (<div key={opt.option_id} className="flex items-center gap-2 rounded-lg px-2.5 py-2" style={{ border: "1px solid #e2e6ef" }}><button type="button" onClick={() => updateOpt(idx, "is_correct", !opt.is_correct)} style={{ width: 18, height: 18, border: opt.is_correct ? "2px solid #0d9488" : "2px solid #c5cbd6", backgroundColor: opt.is_correct ? "#0d9488" : "transparent", borderRadius: s.currentItemType === "mc" ? "50%" : "4px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{opt.is_correct && <Check className="h-2.5 w-2.5 text-white" />}</button><Input value={opt.text} onChange={(e) => updateOpt(idx, "text", e.target.value)} placeholder={`Option ${idx + 1}`} className="h-7 text-xs font-body border-0 focus-visible:ring-0 flex-1 px-1" style={{ color: "#062546" }} /><Input type="number" value={opt.point_value} onChange={(e) => updateOpt(idx, "point_value", parseFloat(e.target.value) || 0)} className="h-7 w-12 text-xs font-body text-center border-0 focus-visible:ring-0" style={{ color: "#062546" }} min="0" /><button type="button" onClick={() => removeOpt(idx)} disabled={s.currentItemOptions.length <= 2} className="text-[#c5cbd6] hover:text-red-400 disabled:opacity-30 p-0.5"><Trash2 className="h-3 w-3" /></button></div>))}<button type="button" onClick={addOpt} className="text-xs font-body font-medium flex items-center gap-1" style={{ color: "var(--primary-color, #09beb2)" }}><Plus className="h-3 w-3" /> Add Option</button></div>)}
        {s.currentItemType === "likert" && (<div className="space-y-3"><div className="grid grid-cols-2 gap-3"><div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Min</Label><Input type="number" value={s.currentItemLikertMin} onChange={(e) => d({ currentItemLikertMin: parseInt(e.target.value) || 0 })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} min="0" max="19" /></div><div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Max</Label><Input type="number" value={s.currentItemLikertMax} onChange={(e) => d({ currentItemLikertMax: Math.min(parseInt(e.target.value) || 0, 20) })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} min="1" max="20" /></div></div><div className="space-y-1.5 max-h-32 overflow-y-auto">{likertRange.map((val) => (<div key={val} className="flex items-center gap-2"><span className="text-xs font-body font-medium w-5 text-center" style={{ color: "#062546" }}>{val}</span><Input value={s.currentItemLikertLabels[String(val)] || ""} onChange={(e) => d({ currentItemLikertLabels: { ...s.currentItemLikertLabels, [String(val)]: e.target.value } })} placeholder={`Label for ${val}`} className="h-7 text-xs font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} /></div>))}</div></div>)}
        {!isExtScore && (<div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Point Value</Label><div className="h-9 px-3 flex items-center rounded-md text-sm font-body w-24" style={{ border: "1px solid #e2e6ef", color: "#062546" }}>{computedPV} <span className="text-[10px] ml-1" style={{ color: "#8896a7" }}>pts</span></div></div>)}
      </div>
    );
    if (isTrackFlow && s.step === 5) return (<div className="space-y-4 mt-4"><div className="rounded-lg p-3" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}><span className="text-xs font-body font-semibold" style={{ color: "#062546" }}>Track: {s.trackName}</span><div className="mt-2 space-y-1">{s.lexps.map((l, i) => (<div key={l.id} className="flex items-center gap-2 text-xs font-body" style={{ color: "#5a6577" }}><span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: "#062546", color: "#fff" }}>{i + 1}</span>{l.title} — {l.itemCount} item{l.itemCount > 1 ? "s" : ""}</div>))}</div></div><p className="text-sm font-body" style={{ color: "#5a6577" }}>Do you want to add another activity to this Track?</p></div>);
    if (isTrackFlow && s.step === 6) return (<div className="space-y-4 mt-4"><p className="text-sm font-body" style={{ color: "#5a6577" }}>Do you want to assign this Track to a cohort?</p>{s.assignCohort === null && (<div className="flex gap-3"><Button onClick={() => { d({ assignCohort: true }); loadCohorts(); }} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>Yes</Button><Button variant="outline" onClick={() => d({ assignCohort: false })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>No, skip</Button></div>)}{s.assignCohort === true && (<div className="space-y-2"><Input value={cohortSearch} onChange={(e) => setCohortSearch(e.target.value)} placeholder="Search cohorts..." className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-cohort-search" /><div className="max-h-40 overflow-y-auto rounded-lg" style={{ border: "1px solid #e2e6ef" }}>{filteredCohorts.length === 0 && <p className="text-xs font-body p-3" style={{ color: "#8896a7" }}>No cohorts found.</p>}{filteredCohorts.map((c) => { const cid = c.id || String(c._id); return (<button key={cid} onClick={() => d({ cohortId: cid })} className="w-full text-left px-3 py-2 text-sm font-body transition-colors hover:bg-black/[0.02] flex items-center justify-between" style={{ borderBottom: "1px solid #f0f2f5", color: "#062546", backgroundColor: s.cohortId === cid ? "#f5f7fa" : "#fff" }}>{c.name}{s.cohortId === cid && <Check className="h-3.5 w-3.5" style={{ color: "#0d9488" }} />}</button>); })}</div></div>)}{s.assignCohort === false && (<p className="text-xs font-body" style={{ color: "#8896a7" }}>No cohort will be assigned. You can assign one later.</p>)}</div>);
    if (isTrackFlow && s.step === 7) return (<div className="space-y-4 mt-4"><p className="text-sm font-body font-medium" style={{ color: "#062546" }}>Review your Track before saving.</p><div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}><div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Track</span><p className="text-base font-body font-bold" style={{ color: "#062546" }}>{s.trackName}</p></div><div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Cohort</span><p className="text-sm font-body" style={{ color: "#062546" }}>{s.cohortId ? (cohortObj?.name || "Selected") : "No cohort assigned"}</p></div><div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Activities ({s.lexps.length})</span><div className="mt-1 space-y-1.5">{s.lexps.map((l, i) => (<div key={l.id} className="flex items-center gap-2"><span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: "#062546", color: "#fff" }}>{i + 1}</span><span className="text-sm font-body" style={{ color: "#062546" }}>{l.title}</span><span className="text-xs font-body" style={{ color: "#8896a7" }}>({l.itemCount} item{l.itemCount > 1 ? "s" : ""})</span></div>))}</div></div></div></div>);
    if (isAssessmentFlow && s.step === 2) return (
      <div className="space-y-4 mt-4">
        <p className="text-sm font-body" style={{ color: "#5a6577" }}>What type of assessment?</p>
        <div className="space-y-3">
          {[
            { v: "native", title: "Native", desc: "All scoring happens inside DiNAXLP through questions and tasks built in the platform" },
            { v: "external", title: "External", desc: "At least one score or item is captured outside the platform, regardless of the tool or system" },
          ].map((o) => (
            <button key={o.v} onClick={() => d({ assessmentSource: o.v })}
              className="w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all"
              style={{ border: s.assessmentSource === o.v ? "2px solid #062546" : "1.5px solid #e2e6ef", backgroundColor: s.assessmentSource === o.v ? "#f5f7fa" : "#fff" }}
              data-testid={`wizard-assess-type-${o.v}`}
            >
              <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
                style={{ backgroundColor: s.assessmentSource === o.v ? "#062546" : "#f0f2f5" }}>
                <ClipboardList className="h-4 w-4" style={{ color: s.assessmentSource === o.v ? "#fff" : "#5a6577" }} />
              </div>
              <div>
                <span className="text-sm font-body font-bold block" style={{ color: "#062546" }}>{o.title}</span>
                <span className="text-xs font-body mt-0.5 block" style={{ color: "#8896a7" }}>{o.desc}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="space-y-1.5 pt-2">
          <Label className="font-body text-sm font-medium" style={{ color: "#062546" }}>Assessment Name <span className="text-red-500">*</span></Label>
          <Input value={s.assessmentName} onChange={(e) => d({ assessmentName: e.target.value })} placeholder="e.g. Pre-Class Quiz" className="h-10 font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-assess-name" />
        </div>
      </div>
    );
    if (isAssessmentFlow && s.assessmentSource === "external" && s.step === 3) return (
      <div className="space-y-4 mt-4">
        <p className="text-sm font-body" style={{ color: "#5a6577" }}>How is the external score captured?</p>
        <div className="space-y-3">
          {[
            { v: "score", title: "Score", desc: "A single aggregate score from an outside tool" },
            { v: "items", title: "Items", desc: "Individual items scored outside the platform" },
          ].map((o) => (
            <button key={o.v} onClick={() => d({ extAssessmentMode: o.v })}
              className="w-full flex items-start gap-4 p-4 rounded-xl text-left transition-all"
              style={{ border: s.extAssessmentMode === o.v ? "2px solid #062546" : "1.5px solid #e2e6ef", backgroundColor: s.extAssessmentMode === o.v ? "#f5f7fa" : "#fff" }}
              data-testid={`wizard-ext-mode-${o.v}`}
            >
              <div>
                <span className="text-sm font-body font-bold block" style={{ color: "#062546" }}>{o.title}</span>
                <span className="text-xs font-body mt-0.5 block" style={{ color: "#8896a7" }}>{o.desc}</span>
              </div>
            </button>
          ))}
        </div>
        {s.extAssessmentMode === "score" && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Correct Answers</Label><Input type="number" min="0" value={s.extScoreCorrect} onChange={(e) => d({ extScoreCorrect: parseFloat(e.target.value) || 0 })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} /></div>
            <div className="space-y-1"><Label className="font-body text-xs" style={{ color: "#5a6577" }}>Total Answers</Label><Input type="number" min="1" value={s.extScoreTotal} onChange={(e) => d({ extScoreTotal: parseFloat(e.target.value) || 0 })} className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} /></div>
          </div>
        )}
      </div>
    );
    if (isAssessmentFlow && s.assessmentSource === "external" && s.extAssessmentMode === "items" && s.step === 4) return (
      <div className="space-y-4 mt-3" style={{ maxHeight: "55vh", overflowY: "auto" }}>
        {s.extItems.length > 0 && (
          <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}>
            <div className="flex items-center gap-2"><Check className="h-3.5 w-3.5" style={{ color: "#0d9488" }} /><span className="text-xs font-body font-semibold" style={{ color: "#062546" }}>{s.extItems.length} item{s.extItems.length > 1 ? "s" : ""} created</span></div>
            {s.extItems.map((it) => (<span key={it.id} className="text-xs font-body block pl-5" style={{ color: "#8896a7" }}>{it.title} ({it.point_value} pts)</span>))}
          </div>
        )}
        <p className="text-sm font-body" style={{ color: "#5a6577" }}>{s.extItems.length === 0 ? "Add at least one external item." : "Add another item, or finish."}</p>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Item Title <span className="text-red-500">*</span></Label><Input value={s.extCurrentTitle} onChange={(e) => d({ extCurrentTitle: e.target.value })} placeholder="e.g. Blood Pressure Check" className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-ext-item-title" /></div>
          <div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Item ID <span className="text-xs font-normal" style={{ color: "#8896a7" }}>(optional — external system identifier)</span></Label><Input value={s.extCurrentItemId} onChange={(e) => d({ extCurrentItemId: e.target.value })} placeholder="e.g. EXT-001" className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-ext-item-id" /></div>
          <div className="space-y-1.5"><Label className="font-body text-xs font-medium" style={{ color: "#062546" }}>Point Value</Label><Input type="number" min="0.5" step="0.5" value={s.extCurrentPointValue} onChange={(e) => d({ extCurrentPointValue: parseFloat(e.target.value) || 0 })} className="h-9 text-sm font-body w-24" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="wizard-ext-item-pv" /></div>
          <p className="text-[10px] font-body" style={{ color: "#8896a7" }}>Response type: correct or incorrect only</p>
        </div>
      </div>
    );
    if (isAssessmentFlow && s.step === 5) return (
      <div className="space-y-4 mt-4">
        <p className="text-sm font-body font-medium" style={{ color: "#062546" }}>Review your assessment.</p>
        <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: "#f5f7fa", border: "1px solid #e2e6ef" }}>
          <div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Assessment</span><p className="text-base font-body font-bold" style={{ color: "#062546" }}>{s.assessmentName}</p></div>
          <div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Type</span><p className="text-sm font-body capitalize" style={{ color: "#062546" }}>{s.assessmentSource}{s.extAssessmentMode ? ` — ${s.extAssessmentMode}` : ""}</p></div>
          {s.assessmentSource === "external" && s.extAssessmentMode === "score" && (
            <div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Score</span><p className="text-sm font-body" style={{ color: "#062546" }}>{s.extScoreCorrect} / {s.extScoreTotal}</p></div>
          )}
          {s.assessmentSource === "external" && s.extAssessmentMode === "items" && s.extItems.length > 0 && (
            <div><span className="text-[10px] font-body uppercase tracking-wider" style={{ color: "#8896a7" }}>Items ({s.extItems.length})</span>
              <div className="mt-1 space-y-1">{s.extItems.map((it, i) => (<div key={it.id} className="flex items-center gap-2 text-xs font-body" style={{ color: "#5a6577" }}><span className="w-4 text-right font-medium" style={{ color: "#062546" }}>{i + 1}.</span>{it.title} — {it.point_value} pts</div>))}</div>
            </div>
          )}
        </div>
      </div>
    );
    return null;
  };

  const renderFooter = () => {
    if (s.step === 1) return (<><Button variant="outline" onClick={tryClose} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }} data-testid="wizard-cancel">Cancel</Button><Button onClick={() => d({ step: 2 })} disabled={!s.intent} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }} data-testid="wizard-continue">Continue</Button></>);
    if (isTrackFlow && s.step === 2) return (<><Button variant="outline" onClick={() => d({ step: 1 })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Back</Button><Button onClick={createTrack} disabled={!s.trackName.trim() || s.saving} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button></>);
    if (isTrackFlow && s.step === 3) return (<><Button variant="outline" onClick={() => d({ step: s.lexps.length > 0 ? 5 : 2 })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Back</Button><Button onClick={createLexp} disabled={!s.currentLexpTitle.trim() || s.saving} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button></>);
    if (isTrackFlow && s.step === 4) return (<><Button onClick={createItem} disabled={s.saving || !s.currentItemTitle.trim()} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Save Item</>}</Button><Button variant="outline" onClick={finishLexp} disabled={s.currentLexpItems.length === 0} className="font-body text-sm" style={{ borderColor: s.currentLexpItems.length > 0 ? "#062546" : "#e2e6ef", color: s.currentLexpItems.length > 0 ? "#062546" : "#c5cbd6" }}>Done with this LEXP</Button></>);
    if (isTrackFlow && s.step === 5) return (<><Button onClick={anotherLexp} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}><Plus className="h-3.5 w-3.5 mr-1" /> Add another activity</Button><Button variant="outline" onClick={() => d({ step: 6 })} disabled={s.lexps.length === 0} className="font-body text-sm" style={{ borderColor: s.lexps.length > 0 ? "#062546" : "#e2e6ef", color: s.lexps.length > 0 ? "#062546" : "#c5cbd6" }}>Finish Track</Button></>);
    if (isTrackFlow && s.step === 6) return (<><Button variant="outline" onClick={() => d({ step: 5 })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Back</Button><Button onClick={s.assignCohort === null ? undefined : assignCohort} disabled={s.assignCohort === null || s.saving} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button></>);
    if (isTrackFlow && s.step === 7) return (<><Button variant="outline" onClick={saveDraft} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Save as Draft</Button><Button onClick={publishTrack} disabled={s.saving} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish Track"}</Button></>);
    if (isAssessmentFlow && s.step === 2) return (<><Button variant="outline" onClick={() => d({ step: 1 })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Back</Button><Button onClick={() => {
      if (!s.assessmentName.trim()) { toast.error("Name required"); return; }
      if (s.assessmentSource === "native") { createAssessment(); } else { d({ step: 3 }); }
    }} disabled={!s.assessmentName.trim()} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>Continue</Button></>);
    if (isAssessmentFlow && s.step === 3) return (<><Button variant="outline" onClick={() => d({ step: 2 })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Back</Button><Button onClick={() => {
      if (!s.extAssessmentMode) return;
      if (s.extAssessmentMode === "score" && s.extScoreTotal <= 0) { toast.error("Total must be > 0"); return; }
      createAssessment();
    }} disabled={!s.extAssessmentMode || s.saving || (s.extAssessmentMode === "score" && s.extScoreTotal <= 0)} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</Button></>);
    if (isAssessmentFlow && s.step === 4) return (<><Button onClick={createExtItem} disabled={s.saving || !s.extCurrentTitle.trim()} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>{s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Save Item</>}</Button><Button variant="outline" onClick={() => d({ step: 5 })} disabled={s.extItems.length === 0} className="font-body text-sm" style={{ borderColor: s.extItems.length > 0 ? "#062546" : "#e2e6ef", color: s.extItems.length > 0 ? "#062546" : "#c5cbd6" }}>Done adding items</Button></>);
    if (isAssessmentFlow && s.step === 5) return (<><Button variant="outline" onClick={() => onClose(false)} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Save as Draft</Button><Button onClick={() => { toast.success("Assessment saved"); onClose(false); onCreated?.(); }} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>Done</Button></>);
    if (isSurveyFlow && s.step === 2) return (
      <>
        <Button variant="outline" onClick={() => d({ step: 1, intent: null })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }} data-testid="wizard-survey-back">Back</Button>
        <Button
          onClick={createSurveyInline}
          disabled={!s.surveyTitle?.trim() || s.saving}
          className="font-body text-sm text-white"
          style={{ backgroundColor: "#062546" }}
          data-testid="wizard-survey-create"
        >
          {s.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & Open Builder"}
        </Button>
      </>
    );
    if (isStubFlow && s.step === 2) return (
      <>
        <Button variant="outline" onClick={() => d({ step: 1, intent: null })} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }} data-testid="wizard-stub-back">Back</Button>
        <Button onClick={goToStubTarget} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }} data-testid="wizard-stub-continue">
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Continue
        </Button>
      </>
    );
    return null;
  };

  const stepLabel = () => {
    if (s.step === 1) return "";
    if (isTrackFlow) return ["", "", "Track Details", "Create Activity", "Add Items", "Add Another?", "Assign Cohort", "Review"][s.step] || "";
    if (isAssessmentFlow) return ["", "", "Assessment Type", "Capture Method", "Add Items", "Review"][s.step] || "";
    if (isLexpFlow) return "Open Builder";
    if (isItemFlow) return "Open Builder";
    if (isSurveyFlow) return "Survey Details";
    return "";
  };

  return (
    <>
      <Dialog open={open && !confirmExit} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden" style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef", borderRadius: "16px" }} data-testid="wizard-modal">
          <div className="px-6 pt-6 pb-4">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold" style={{ color: "#062546" }} data-testid="wizard-title">What do you want to build?</DialogTitle></DialogHeader>
            <StepDots current={s.step} total={totalDots} />
            {s.step > 1 && <p className="text-[10px] font-body uppercase tracking-wider mt-2" style={{ color: "#8896a7" }}>Step {s.step} — {stepLabel()}</p>}
            {renderStep()}
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: "#e2e6ef" }}>{renderFooter()}</div>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmExit} onOpenChange={setConfirmExit}>
        <DialogContent className="sm:max-w-sm" style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef", borderRadius: "16px" }}>
          <DialogHeader><DialogTitle className="font-body text-base font-bold" style={{ color: "#062546" }}>Discard progress?</DialogTitle></DialogHeader>
          <p className="text-sm font-body mt-2" style={{ color: "#5a6577" }}>You have unsaved progress. Are you sure you want to exit?</p>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setConfirmExit(false)} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Keep working</Button>
            <Button onClick={() => { setConfirmExit(false); onClose(false); }} className="font-body text-sm text-white" style={{ backgroundColor: "#dc2626" }}>Discard</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function CurriculumManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistentState("curriculum:search", "");
  const [statusFilter, setStatusFilter] = usePersistentState("curriculum:statusFilter", "all");
  const [expandedTracks, setExpandedTracks] = useState(new Set());
  const [expandedLexpByTrack, setExpandedLexpByTrack] = useState({});
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPreselect, setWizardPreselect] = useState(null);
  const [cohortModalTrackId, setCohortModalTrackId] = useState(null);
  const [cohortList, setCohortList] = useState([]);
  const [cohortModalSearch, setCohortModalSearch] = useState("");
  const [cohortModalSelected, setCohortModalSelected] = useState(null);
  const [cohortAssigning, setCohortAssigning] = useState(false);
  const [dupWizardOpen, setDupWizardOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/curriculum/tree`, { withCredentials: true });
      setData(d);
    } catch {
      toast.error("Failed to load curriculum data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── expand/collapse ─── */
  const toggleTrack = (id) => {
    setExpandedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLexp = (trackId, lexpRefId) => {
    setExpandedLexpByTrack((prev) => ({
      ...prev,
      [trackId]: prev[trackId] === lexpRefId ? null : lexpRefId,
    }));
  };

  const expandAll = () => {
    if (!data) return;
    setExpandedTracks(new Set(data.tracks.map((t) => t.id)));
  };

  const collapseAll = () => {
    setExpandedTracks(new Set());
    setExpandedLexpByTrack({});
  };

  const allExpanded = data ? expandedTracks.size === data.tracks.length : false;

  /* ─── wizard ─── */
  const openWizard = (preselect, trackId) => {
    setWizardPreselect(preselect || null);
    setWizardOpen(true);
  };

  /* ─── cohort assignment modal ─── */
  const openCohortModal = async (trackId) => {
    setCohortModalTrackId(trackId);
    setCohortModalSearch("");
    setCohortModalSelected(null);
    setCohortAssigning(false);
    try {
      const { data: c } = await axios.get(`${API}/cohorts`, { withCredentials: true });
      setCohortList(c);
    } catch { setCohortList([]); }
  };
  const closeCohortModal = () => setCohortModalTrackId(null);
  const confirmCohortAssign = async () => {
    if (!cohortModalSelected || !cohortModalTrackId) return;
    setCohortAssigning(true);
    try {
      await axios.post(`${API}/cohorts/${cohortModalSelected}/tracks`, { track_id: cohortModalTrackId }, { withCredentials: true });
      toast.success("Track assigned to cohort");
      closeCohortModal();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
    finally { setCohortAssigning(false); }
  };
  const filteredCohortList = cohortList.filter((c) => c.name?.toLowerCase().includes(cohortModalSearch.toLowerCase()));

  /* ─── filtering ─── */
  const filteredTracks = (data?.tracks || []).filter((t) => {
    // Status filter
    if (statusFilter === "published" && t.derived_status !== "published") return false;
    if (statusFilter === "draft" && t.derived_status !== "draft") return false;
    if (statusFilter === "incomplete" && t.derived_status !== "incomplete") return false;
    // Search filter
    if (search) {
      const q = search.toLowerCase();
      const trackMatch = t.title.toLowerCase().includes(q);
      const lexpMatch = t.children.some((c) => c.title?.toLowerCase().includes(q));
      return trackMatch || lexpMatch;
    }
    return true;
  });

  /* ─── stats ─── */
  const stats = data?.stats || { total_tracks: 0, total_lexps: 0, total_assessments: 0, incomplete: 0 };

  const filterPills = [
    { key: "all", label: "All" },
    { key: "published", label: "Published" },
    { key: "draft", label: "Draft" },
    { key: "incomplete", label: "Incomplete" },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #09beb2)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="curriculum-manager-page">
      {/* ─── Topbar ─── */}
      <div className="rounded-xl px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        style={{ backgroundColor: "#fdf0ed" }} data-testid="curriculum-topbar">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: "#062546" }} data-testid="curriculum-title">
          Curriculum Manager
        </h2>
        <div className="flex items-center gap-3">
          <DefinitionsPopover />
          <Button
            className="font-body text-sm text-white"
            style={{ backgroundColor: "#062546" }}
            onClick={() => setDupWizardOpen(true)}
            data-testid="duplication-wizard-btn"
          >
            <Copy className="h-4 w-4 mr-1.5" /> Duplication Wizard
          </Button>
          <Button
            className="font-body text-sm text-white"
            style={{ backgroundColor: "#062546" }}
            onClick={() => openWizard(null)}
            data-testid="learning-wizard-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Learning Wizard
          </Button>
        </div>
      </div>

      {/* ─── Stats Row ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="curriculum-stats">
        {[
          { label: "Total Tracks", value: stats.total_tracks, icon: Layers, color: "#062546" },
          { label: "Total LEXPs", value: stats.total_lexps, icon: BookOpen, color: "#0d9488" },
          { label: "Total Assessments", value: stats.total_assessments, icon: ClipboardList, color: "#7c3aed" },
          { label: "Incomplete", value: stats.incomplete, icon: AlertTriangle, color: stats.incomplete > 0 ? "#dc2626" : "#8896a7" },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl px-4 py-4 flex items-center gap-4"
            style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef" }}
            data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${s.color}10` }}>
              <s.icon className="h-5 w-5" style={{ color: s.color }} />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs font-body" style={{ color: "#8896a7" }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Filter Bar ─── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="curriculum-filters">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#8896a7" }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tracks or LEXPs..."
            className="pl-9 h-9 text-sm font-body"
            style={{ backgroundColor: "#fff", borderColor: "#e2e6ef", color: "#062546" }}
            data-testid="curriculum-search"
          />
        </div>
        <div className="flex items-center gap-1.5">
          {filterPills.map((p) => (
            <button
              key={p.key}
              onClick={() => setStatusFilter(p.key)}
              className="px-3 py-1.5 rounded-full text-xs font-body font-medium transition-colors"
              style={{
                backgroundColor: statusFilter === p.key ? "#062546" : "#fff",
                color: statusFilter === p.key ? "#fff" : "#5a6577",
                border: statusFilter === p.key ? "none" : "1px solid #e2e6ef",
              }}
              data-testid={`filter-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={allExpanded ? collapseAll : expandAll}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body font-medium transition-colors ml-auto"
          style={{ backgroundColor: "#fff", color: "#5a6577", border: "1px solid #e2e6ef" }}
          data-testid="expand-collapse-all"
        >
          <ChevronsUpDown className="h-3.5 w-3.5" />
          {allExpanded ? "Collapse all" : "Expand all"}
        </button>
      </div>

      {/* ─── Curriculum Tree ─── */}
      <div className="space-y-4" data-testid="curriculum-tree">
        {filteredTracks.length === 0 && (
          <div className="text-center py-16 rounded-xl" style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef" }}>
            <Layers className="h-10 w-10 mx-auto mb-3" style={{ color: "#c5cbd6" }} />
            <p className="font-body text-sm" style={{ color: "#8896a7" }}>
              {search || statusFilter !== "all" ? "No tracks match your filters." : "No tracks yet. Create your first track to get started."}
            </p>
          </div>
        )}
        {filteredTracks.map((track) => (
          <TrackCard
            key={track.id}
            track={track}
            isExpanded={expandedTracks.has(track.id)}
            onToggleTrack={toggleTrack}
            expandedLexp={expandedLexpByTrack[track.id] || null}
            onToggleLexp={toggleLexp}
            onOpenWizard={openWizard}
            onAssignCohort={openCohortModal}
          />
        ))}
      </div>

      {/* ─── Wizard Modal ─── */}
      <WizardModal open={wizardOpen} onClose={setWizardOpen} preselect={wizardPreselect} onCreated={(trackId) => {
        fetchData();
        if (trackId) setExpandedTracks((prev) => new Set([...prev, trackId]));
      }} />

      {/* ─── Cohort Assignment Modal ─── */}
      <Dialog open={!!cohortModalTrackId} onOpenChange={(v) => { if (!v) closeCohortModal(); }}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden" style={{ backgroundColor: "#fff", border: "1px solid #e2e6ef", borderRadius: "16px" }} data-testid="cohort-assign-modal">
          <div className="px-6 pt-6 pb-4 space-y-4">
            <DialogHeader><DialogTitle className="font-body text-base font-bold" style={{ color: "#062546" }}>Assign to Cohort</DialogTitle></DialogHeader>
            <Input value={cohortModalSearch} onChange={(e) => setCohortModalSearch(e.target.value)} placeholder="Search cohorts..." className="h-9 text-sm font-body" style={{ borderColor: "#e2e6ef", color: "#062546" }} data-testid="cohort-assign-search" />
            <div className="max-h-48 overflow-y-auto rounded-lg" style={{ border: "1px solid #e2e6ef" }}>
              {filteredCohortList.length === 0 && <p className="text-xs font-body p-3" style={{ color: "#8896a7" }}>No cohorts found.</p>}
              {filteredCohortList.map((c) => {
                const cid = c.id || String(c._id);
                return (
                  <button key={cid} onClick={() => setCohortModalSelected(cid)}
                    className="w-full text-left px-3 py-2 text-sm font-body transition-colors hover:bg-black/[0.02] flex items-center justify-between"
                    style={{ borderBottom: "1px solid #f0f2f5", color: "#062546", backgroundColor: cohortModalSelected === cid ? "#f5f7fa" : "#fff" }}
                    data-testid={`cohort-option-${cid}`}
                  >
                    {c.name}
                    {cohortModalSelected === cid && <Check className="h-3.5 w-3.5" style={{ color: "#0d9488" }} />}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: "#e2e6ef" }}>
            <Button variant="outline" onClick={closeCohortModal} className="font-body text-sm" style={{ borderColor: "#e2e6ef", color: "#5a6577" }}>Cancel</Button>
            <Button onClick={confirmCohortAssign} disabled={!cohortModalSelected || cohortAssigning} className="font-body text-sm text-white" style={{ backgroundColor: "#062546" }}>
              {cohortAssigning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DuplicationWizard open={dupWizardOpen} onClose={() => setDupWizardOpen(false)} />
    </div>
  );
}
